import { IPatient } from "../patient/patient.types";

/**
 * Core request interface based on OpenAPI specification
 */
export interface IMedicalRequest {
    entityId: string;                          // Required UUID
    zTouchTimestamp?: string;                  // Legacy modification timestamp (write-only)
    zCreationTimestamp?: string;               // Legacy creation timestamp (write-only)
    status: RequestStatus;                     // Request status (required)
    speciality: string;                        // Medical speciality (required) - now string from API
    requestType: string;                       // Request type (required) - now string from API
    content: IMedicalRequestContent;          // Request content (required)
    isRead?: boolean;                         // Read status
    clinicianId: string;                      // Clinician ID (required UUID)
    patientId: string;                        // Patient ID (required UUID)
    handleBy?: string;                        // Expert assigned UUID
    clinician?: IClinician;                   // Populated clinician data
    patient?: IPatient;                       // Populated patient data
    expertAssigned?: IClinician;              // Populated expert data
    requestStatusDates?: IRequestStatusDates; // Status transition dates
    ztouchTimestamp: string;                  // Normalized modification timestamp (required)
    zcreationTimestamp: string;               // Creation timestamp (required)
}

/**
 * Medical request content interface
 */
export interface IMedicalRequestContent {
    items: IMedicalRequestContentItem[];      // Array of content items (required)
}

/**
 * Medical request content item interface
 */
export interface IMedicalRequestContentItem {
    name: string;                             // Field name (required)
    type: ContentItemType;                    // Field type (required)
    value: string;                            // Field value (required)
}

/**
 * Content item type enumeration
 */
export const ContentItemTypes = ['TEXT', 'FILE'] as const;
export type ContentItemType = typeof ContentItemTypes[number];

/**
 * Request status enumeration from OpenAPI spec
 */
export const RequestStatuses = ['DRAFT', 'WAITING', 'IN_PROGRESS', 'COMPLETED', 'ARCHIVED'] as const;
export type RequestStatus = typeof RequestStatuses[number];

/**
 * Request status dates interface - tracks when request entered each status
 */
export interface IRequestStatusDates {
    DRAFT: string | null;
    WAITING: string | null;
    IN_PROGRESS: string | null;
    COMPLETED: string | null;
    ARCHIVED: string | null;
}

/**
 * Basic clinician interface for population
 */
export interface IClinician {
    entityId?: string;
    zTouchTimestamp?: string;
    zCreationTimestamp?: string;
    userId?: string;
    firstName?: string;
    lastName: string;
    gender: 'MALE' | 'FEMALE' | 'OTHER';
    function?: string;
    speciality?: string;                      // Now string from API
    jobTitle?: string;
    medicalEntity?: string;
    city?: string;
    country?: string;
    phone1: string;
    phone2?: string;
    email: string;
    profilePicture?: string;
    roles?: string[];                         // Array of role strings
    ztouchTimestamp?: string;
    zcreationTimestamp?: string;
}

/**
 * Request filter interface - UPDATED with new swagger parameters
 */
export interface IRequestFilter {
    search?: string;                          // Search term for patient name/request info
    f_status?: RequestStatus;                 // Filter by status
    f_speciality?: string;                    // Filter by speciality (expertisearea in API)
    f_request_type?: string;                  // Filter by request type (NEW from swagger)
    f_reading?: boolean;                      // Filter by reading status (NEW from swagger)
    sort?: string;                           // Sort field and direction
    page?: number;                           // Page number (default: 1)
    per_page?: number;                       // Items per page (default: 20)
}

/**
 * Spring Data Page response structure for medical requests
 */
export interface IPageMedicalRequestOut {
    totalElements: number;             // Total number of items across all pages
    totalPages: number;               // Total number of pages
    size: number;                     // Page size (items per page)
    content: IMedicalRequest[];       // Request data array for current page
    number: number;                   // Current page number (0-based)
    sort: ISortObject;               // Sort information
    first: boolean;                   // Is this the first page?
    last: boolean;                    // Is this the last page?
    numberOfElements: number;         // Number of elements in current page
    pageable: IPageableObject;       // Pageable configuration
    empty: boolean;                   // Is the page empty?
}

/**
 * Sort object from Spring Data
 */
export interface ISortObject {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
}

/**
 * Pageable object from Spring Data
 */
export interface IPageableObject {
    offset: number;
    sort: ISortObject;
    pageSize: number;
    pageNumber: number;
    paged: boolean;
    unpaged: boolean;
}

/**
 * Converted pagination response for easier frontend use
 */
export interface IRequestPagination {
    currentPage: number;              // 1-based current page number
    pageSize: number;                // Items per page
    totalItems: number;              // Total number of items
    totalPages: number;              // Total number of pages
    hasNext: boolean;                // Has next page
    hasPrevious: boolean;            // Has previous page
    isFirst: boolean;                // Is first page
    isLast: boolean;                 // Is last page
    numberOfElements: number;        // Elements in current page
}

/**
 * Request list response interface
 */
export interface IRequestListResponse extends IPageMedicalRequestOut {
    // Extends the Spring Data Page structure directly
}

/**
 * Request creation interface for ECG requests - UPDATED
 */
export interface IECGRequestCreate {
    message: string;                          // Request message (required)
    speciality: string;                       // Medical speciality (required) - now string
    requestType: string;                      // Request type (required) - now string  
    patientId: string;                       // Patient ID (required)
    personalHistory?: string;                // Personal history (from swagger)
    familyHistory?: string;                  // Family history (from swagger)
    cardiovascularRiskFactors?: string;      // Cardiovascular risk factors (from swagger)
    regularMedications?: string;             // Regular medications (from swagger)
    functionalSymptoms?: string;             // Functional symptoms (from swagger)
    clinicalSigns?: string;                  // Clinical signs (from swagger)
}

/**
 * Dermatology request: a patient, the question and one or more files (skin photos...).
 * The expertise area and request type are set by the backend.
 */
export interface IDermatologyRequestSave {
    message: string;
    patientId: string;
    /** New files to attach (on update, the files already attached are kept). */
    files: File[];
    /** Update only: ids of attached files to remove. */
    removedFileIds?: string[];
}

/**
 * Request update interface for ECG requests - UPDATED
 */
export interface IECGRequestUpdate extends IECGRequestCreate {
    // Inherits all fields from IECGRequestCreate
}

/**
 * ECG Request Form interface for iOS JSON format
 * Used when sending requests on iOS platform with base64 encoded file
 * Note: Backend expects 'expertiseArea' (camelCase) instead of 'speciality'
 * Note: Backend expects 'fileB64' instead of 'file' for the base64 encoded file
 */
export interface ECGRequestForm {
    message: string;
    expertiseArea: string; // Backend expects expertiseArea (camelCase), not speciality
    requestType: string;
    patientId: string;
    personalHistory?: string;
    familyHistory?: string;
    cardiovascularRiskFactors?: string;
    regularMedications?: string;
    functionalSymptoms?: string;
    clinicalSigns?: string;
    fileB64?: string; // Base64 encoded file for iOS (backend expects fileB64, not file)
    fileName?: string; // Original file name
    fileType?: string; // MIME type of the file
}

/**
 * Request statistics interface - UPDATED for API strings
 */
export interface IRequestStats {
    totalRequests: number;                   // Total number of requests
    draftCount: number;                      // Requests in draft
    waitingCount: number;                    // Requests waiting
    inProgressCount: number;                 // Requests in progress
    completedCount: number;                  // Completed requests
    archivedCount: number;                   // Archived requests
    bySpeciality: { [key: string]: number }; // Using string keys for API compatibility
    byRequestType: { [key: string]: number }; // Using string keys for API compatibility
}

/**
 * Medical speciality output interface from API
 */
export interface IMedicalSpecialityOut {
    name: string;                            // Speciality name (required)
    requestTypes: string[];                  // Available request types (required)
}

/**
 * Helper function to convert Spring Data Page to frontend pagination
 */
export function convertPageToPagination(page: IPageMedicalRequestOut): IRequestPagination {
    return {
        currentPage: page.number + 1,        // Convert from 0-based to 1-based
        pageSize: page.size,
        totalItems: page.totalElements,
        totalPages: page.totalPages,
        hasNext: !page.last,
        hasPrevious: !page.first,
        isFirst: page.first,
        isLast: page.last,
        numberOfElements: page.numberOfElements
    };
}